import { useState, useEffect } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { apiClient } from '@/lib/api'

interface ServiceModalProps {
  isOpen: boolean
  onClose: () => void
  service: any | null
  tenantId: string
  onSuccess: () => void
}

export function ServiceModal({ isOpen, onClose, service, tenantId, onSuccess }: ServiceModalProps) {
  const [formData, setFormData] = useState({
    service_id: '',
    name: '',
    description: '',
    estimated_minutes: 15
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (service) {
      setFormData({
        service_id: service.service_id || '',
        name: service.name || '',
        description: service.description || '',
        estimated_minutes: service.estimated_minutes || 15
      })
    } else {
      setFormData({ service_id: '', name: '', description: '', estimated_minutes: 15 })
    }
  }, [service, isOpen])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    try {
      setSaving(true)
      
      if (service) {
        const { service_id, ...updateData } = formData
        await apiClient.updateService(tenantId, service_id, updateData)
      } else {
        await apiClient.createService(tenantId, formData)
      }
      
      onSuccess()
    } catch (err: any) {
      const errorMsg = err.response?.data?.message || err.message || 'Failed to save service'
      alert(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{service ? 'Edit Service' : 'Add Service'}</DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="service_id">Service ID *</Label>
            <Input
              id="service_id"
              value={formData.service_id}
              onChange={(e) => {
                const sanitized = e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_-]/g, '')
                setFormData(prev => ({ ...prev, service_id: sanitized }))
              }}
              placeholder="general-inquiry"
              required
              disabled={!!service}
            />
            <p className="text-xs text-gray-500">Auto-formatted: lowercase, spaces→underscores (cannot be changed after creation)</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
              placeholder="General Inquiry"
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="General customer service inquiries"
              rows={3}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="estimated_minutes">Estimated Service Time (minutes)</Label>
            <Input
              id="estimated_minutes"
              type="number"
              min="1"
              max="120"
              value={formData.estimated_minutes}
              onChange={(e) => setFormData(prev => ({ ...prev, estimated_minutes: parseInt(e.target.value) || 15 }))}
              required
            />
            <p className="text-xs text-gray-500">Used for wait time predictions (1-120 minutes)</p>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving...' : service ? 'Update' : 'Create'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  )
}
